import apiService from './apiService';
import { GET } from './executer';

export interface Session {
  season_year: number;
  circuit_id: string;
  start_date: string; // ISO date format (YYYY-MM-DD)
  session_name: string;
  start_time: string;
  end_time: string;
  description: string | null;
}

export interface SessionsResponse {
  success: boolean;
  data: Session[];
  count: number;
}

class SessionsService {
  /**
   * Get all sessions with optional filters (cross-event queries)
   * For single event queries, prefer getSessionsByEvent
   */
  async getSessions(params?: {
    season_year?: number;
    circuit_id?: string;
    session_name?: string;
  }): Promise<Session[]> {
    try {
      const queryString = params 
        ? '?' + new URLSearchParams(
            Object.entries(params)
              .filter(([, value]) => value !== undefined)
              .map(([key, value]) => [key, String(value)])
          ).toString()
        : '';
      
      // Uses /api/events/sessions for cross-event queries
      // Sessions are embedded in events and UNNESTed in the query
      const response = await apiService.client.execute<Session[]>(
        GET,
        `/api/events/sessions${queryString}`
      );
      
      if (response.success && response.data) {
        // Execute method unwraps response.data.data to response.data
        return response.data;
      }
      throw new Error('Failed to fetch sessions');
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  }

  /**
   * Get all sessions for a specific event
   * Now uses the events API with embedded sessions for better data consistency
   */
  async getSessionsByEvent(
    seasonYear: number, 
    circuitId: string, 
    startDate: string
  ): Promise<Session[]> {
    try {
      // Use the new lightweight sessions endpoint on events route
      const url = `/api/events/${seasonYear}/${circuitId}/${startDate}/sessions`;
      const response = await apiService.client.execute<Session[]>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        // Execute method unwraps response.data.data to response.data
        return response.data;
      }
      
      // If no success but no error data, return empty array (no sessions found)
      if (!response.errorData) {
        return [];
      }
      
      // Check if it's a 404 (no sessions for this event)
      const errorCode = response.errorData?.code;
      if (errorCode === 404 || errorCode === 'ERR_NETWORK') {
        console.log(`No sessions found for ${circuitId} ${seasonYear} ${startDate}`);
        return []; // Return empty array instead of throwing
      }
      
      throw new Error(`Failed to fetch sessions for ${circuitId} ${seasonYear} ${startDate}`);
    } catch (error) {
      console.error(`Error fetching sessions for ${circuitId} ${seasonYear} ${startDate}:`, error);
      // Return empty array instead of throwing to gracefully handle missing data
      return [];
    }
  }

  /**
   * Format session time to circuit local time
   * @param timestamp - UTC timestamp string
   * @param timeZone - IANA timezone (e.g., "Europe/Rome"), defaults to browser timezone
   */
  formatSessionTime(timestamp: string, timeZone?: string): string {
    const date = new Date(timestamp);
    
    return date.toLocaleTimeString('en-US', {
      timeZone: timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  /**
   * Format session date in circuit local time
   * @param timestamp - UTC timestamp string
   * @param timeZone - IANA timezone (e.g., "Europe/Rome"), defaults to browser timezone
   */
  formatSessionDate(timestamp: string, timeZone?: string): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      timeZone: timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * Get session display name (friendly name)
   */
  getSessionDisplayName(sessionName: string): string {
    const nameMap: Record<string, string> = {
      FP1: 'Free Practice 1',
      FP2: 'Free Practice 2',
      FP3: 'Free Practice 3',
      Q: 'Qualifying',
      SQ: 'Sprint Qualifying',
      SPRINT: 'Sprint Race',
      RACE: 'Race',
    };

    return nameMap[sessionName] || sessionName;
  }

  /**
   * Get session icon/emoji
   */
  getSessionIcon(sessionName: string): string {
    const iconMap: Record<string, string> = {
      FP1: '🏁',
      FP2: '🏁',
      FP3: '🏁',
      Q: '⚡',
      SQ: '⚡',
      SPRINT: '🏎️',
      RACE: '🏆',
    };

    return iconMap[sessionName] || '📍';
  }
}

export const sessionsService = new SessionsService();
export default sessionsService;

